/* @flow */

import React, { useContext, useState } from "react";
import { Link } from "react-router-dom";
import { Route, Switch, useHistory, useLocation } from "react-router";
import { usePreserveScrollPosition } from "helpers/react-router-on-navigation";
import { UiProvider, useUi, MODE } from "helpers/ui";
import Header from "app/Header";
import Routes from "routes";
import { CmsPageData, CmsBlockData, AdventCalendarData } from "state/data";
import { useTranslate } from "@awardit/react-use-translate";
import { Helmet } from "react-helmet-async";
import { StoreInfoContext } from "entrypoint/shared";
import { NotificationBar, AppFooter as AppFooterExt, Button, SkipToContent } from "@crossroads/ui-components";
import HashDialouge from "./HashDialouge";
import useSponsor from "helpers/hooks/use-sponsor";
import useCookie from "helpers/hooks/use-cookie";
import CookieConsent from "components/CookieConsent";
import Logo from "components/Logo";
import { parseParams } from "helpers/location-search-string";

import styles from "./styles.scss";

const App = (): React$Node => {
  const location = useLocation();
  const history = useHistory();
  const { hash, club } = useSponsor();
  const { info } = useContext(StoreInfoContext);
  const onCheckout = (location ? location.pathname : "").includes("checkout");
  const t = useTranslate();
  const { password, testDate } = parseParams(location.search);

  usePreserveScrollPosition(history);

  const cookieOpts = { domain: `.${typeof window !== "undefined" ? window.location.host.replace(/:\d+$/, "") : ""}`, expires: 3650 };
  const [consent, setConsent, consentRead] = useCookie("cookie-consent", "initial", cookieOpts);
  const [toggleCookieDialogue, setToggleCookieDialogue] = useState(false);
  const [hideNotificationBar, setHideNotificationBar] = useState(false);

  const footerTerms = () => (
    <div className={styles.termsField}>
      <div>
        <Link className={styles.link} to="/villkor">
          {t("FOOTER.TERMS")}
        </Link>
        &nbsp;|&nbsp;
        <Button
          aria-expanded={toggleCookieDialogue ?? "true"}
          aria-controls="cookieDialogue"
          className={styles.link}
          onClick={() => setToggleCookieDialogue(true)}
        >
          {t("FOOTER.COOKIES")}
        </Button>
        &nbsp;|&nbsp;
        <Link className={styles.link} to="/accessibility">
          {t("FOOTER.ACCESSIBILITY")}
        </Link>
      </div>
      <p dangerouslySetInnerHTML={{ __html: t("FOOTER.COPYRIGHT", { year: new Date().getFullYear() }) }} />
    </div>
  );

  return (
    <AdventCalendarData.Provider
      testDate={typeof testDate === "string" ? testDate : null}
      password={typeof password === "string" ? password : null}
    >
      <UiProvider>
        <CmsPageData.Provider url="cookie-consent">
          <CmsBlockData.Provider identifier="mini-banner">
            <Helmet
              titleTemplate={`${info.titlePrefix || ""} %s ${info.titleSuffix || ""}`}
              link={[
                { rel: "apple-touch-icon", sizes: "57x57", href: "/assets/browser/apple-touch-icon.png" },
                { rel: "apple-touch-icon", sizes: "60x60", href: "/assets/browser/apple-touch-icon.png" },
                { rel: "apple-touch-icon", sizes: "72x72", href: "/assets/browser/apple-touch-icon.png" },
                { rel: "apple-touch-icon", sizes: "76x76", href: "/assets/browser/apple-touch-icon.png" },
                { rel: "apple-touch-icon", sizes: "114x114", href: "/assets/browser/apple-touch-icon.png" },
                { rel: "apple-touch-icon", sizes: "120x120", href: "/assets/browser/apple-touch-icon.png" },
                { rel: "apple-touch-icon", sizes: "144x144", href: "/assets/browser/apple-touch-icon.png" },
                { rel: "apple-touch-icon", sizes: "152x152", href: "/assets/browser/apple-touch-icon.png" },
                { rel: "apple-touch-icon", sizes: "180x180", href: "/assets/browser/apple-touch-icon.png" },
                { rel: "icon", type: "image/png", size: "16x16", href: "/assets/browser/favicon-16x16.png" },
                { rel: "icon", type: "image/png", size: "32x32", href: "/assets/browser/favicon-32x32.png" },
                { rel: "icon", type: "image/png", size: "48x48", href: "/assets/browser/favicon-48x48.png" },
                { rel: "manifest", href: "/manifest.json" },
                { rel: "mask-icon", href: "/assets/browser/safari-pinned-tab.svg", color: "#da2911" },
              ]}
              meta={[
                { name: "msapplication-TileColor", content: "#ffffff" },
                { name: "theme-color", content: "#ffffff" },
              ]}
              htmlAttributes={{ lang: info.locale.slice(0, 2) }}
            />
            <Switch>
              <Route
                render={() => (
                  <div className={styles.app}>
                    <SkipToContent />

                    {consentRead &&
                      <div className={styles.notices}>
                        <CookieConsent
                          country={info.defaultCountry}
                          isVisible={
                            (hash.length > 0 || club.length > 0) && consentRead &&
                            (consent === "initial" || toggleCookieDialogue)
                          }
                          consent={consent}
                          setConsent={setConsent}
                          consentRead={consentRead}
                          setToggleCookieDialogue={setToggleCookieDialogue}
                        />
                      </div>
                    }

                    <Header />

                    <main id="main" className={styles.mainContent}>
                      <HashDialouge />
                      <Routes />
                    </main>

                    {!onCheckout &&
                      <AppFooterExt
                        hasVisibleHelpColumn
                        activeLinks={["start", "categories", "search"]}
                        className={styles.footer}
                        contentTerms={footerTerms}
                        contentBranding={() => <Logo className={styles.logo} />}
                        MODE={MODE}
                        useUi={useUi}
                      />
                    }

                    {info.popUp && info.popUp.body && !hideNotificationBar &&
                      <NotificationBar
                        closeBtnTitle={t("NOTIFICATIONBAR.CLOSE")}
                        className={styles.notificationBar}
                        text={info.popUp.body}
                        onClose={() => setHideNotificationBar(true)}
                      />
                    }
                  </div>
                )}
              />
            </Switch>
          </CmsBlockData.Provider>
        </CmsPageData.Provider>
      </UiProvider>
    </AdventCalendarData.Provider>
  );
};

export default App;
